import React, { useState } from 'react';
import ScrollerNav from './ScrollerNav';
import NavSection from './NavSection';
import { NavItem } from './types/NavItem';

/**
 * A hook for rendering a navigation bar that scrolls to specific sections of the page.
 * @param navTitle Title to show on the nav bar
 * @param navItems Sections of the page we want to disply
 * @param navBarCtaInfo info for the call-to-action button on the nav bar. If ommitted, don't render it.
 * @returns A tuple of functions to render the nav bar & render corresponding sections based off of the navItem id.
 */
const useScrollerNav = (
  navTitle: string,
  navItems: NavItem[],
  navBarCtaInfo?: { title: string; onClick: () => void }
): { renderNavBar: () => JSX.Element; renderSection: (id: string, children: React.ReactNode) => JSX.Element; selectedNavIndex: number } => {
  const [selectedNavIndex, setSelectedNavIndex] = useState<number>(0);
  const navItemMap = React.useMemo(() => {
    const indexMap = {} as { [key: string]: number };
    navItems.forEach((item: NavItem, idx: number) => {
      indexMap[item.id] = idx;
    });
    return indexMap;
  }, [navItems]);

  const renderNavBar = () => (
    <ScrollerNav
      items={navItems}
      title={navTitle}
      selectedTabIndex={selectedNavIndex}
      onTabIndexChange={setSelectedNavIndex}
      ctaInfo={navBarCtaInfo}
    />
  );

  const renderSection = (id: string, children: React.ReactNode) => (
    <NavSection id={id} setAsSelectedSection={() => setSelectedNavIndex(navItemMap[id])}>
      {children}
    </NavSection>
  );

  return { renderNavBar, renderSection, selectedNavIndex };
};

export default useScrollerNav;
